/**
 * ImageCropperModal — auto-opens after a restaurant owner picks a food
 * photo. Auto-mode (default) zooms / centers to a 4:3 frame matching the
 * menu card thumbnail aspect (square crop happens at render time via
 * `object-cover` so the same source serves both card + product detail
 * gracefully). Manual mode lets the owner drag + pinch / wheel to zoom.
 *
 * UX inspired by Uber Eats / DoorDash photo upload:
 *   • Dark overlay
 *   • Live pan + zoom (mouse, wheel, touch pinch — all native via
 *     `react-easy-crop`)
 *   • "Safe zone" gold rule-of-thirds overlay so the food is centered
 *   • Side-by-side previews (Card · Detail · Mobile) updating live
 *   • Single "Save" CTA emits a compressed JPEG data URI to the parent
 *
 * The parent component decides what to do with the resulting data URI
 * (state set, autosave loop pushes it to /api/restaurants/...; the
 * backend then transparently migrates the URI to Cloudinary with
 * `quality:auto,fetch_format:auto` transformations applied).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import { ZoomIn, ZoomOut, RotateCcw, X, Check, Smartphone, Image as ImgIcon } from 'lucide-react';
import { getCroppedImg } from '../utils/imageCrop';

// Menu card thumbnails are square (object-cover crops the centre out of
// the 4:3 source — see PublicMenu.jsx render). 4:3 is our authoring
// aspect because it works for both burgers/pizzas (square-friendly) and
// drinks/desserts (taller items). Source of truth lives here.
const DEFAULT_ASPECT = 4 / 3;

const ImageCropperModal = (props) => {
  // We key the inner component on `src` so React fully resets crop/zoom/
  // area state whenever a new image is loaded — no `useEffect` →
  // `setState` antipattern needed.
  if (!props.open) return null;
  return <ImageCropperInner key={props.src || 'empty'} {...props} />;
};

const ImageCropperInner = ({
  open,
  src,
  aspect = DEFAULT_ASPECT,
  title = 'Adjust your food photo',
  onSave,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [previewUri, setPreviewUri] = useState(null);
  const [saving, setSaving] = useState(false);

  // Live re-render of the preview row as the crop changes. Cheap —
  // only builds a small 320px-wide JPEG, never the full output.
  useEffect(() => {
    if (!src || !area) return undefined;
    let cancelled = false;
    getCroppedImg(src, area, { maxWidth: 320, quality: 0.78 })
      .then((u) => { if (!cancelled) setPreviewUri(u); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [src, area]);

  const onCropComplete = useCallback((_pct, pixels) => setArea(pixels), []);

  // Body-scroll lock while the modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); };

  const save = async () => {
    if (!area || !src) return;
    setSaving(true);
    try {
      const dataUri = await getCroppedImg(src, area, { maxWidth: 1600, quality: 0.86 });
      onSave?.(dataUri);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="image-cropper-modal"
      style={{ width: '100vw', height: '100dvh' }}
    >
      <div
        className="bg-zinc-950 border border-yellow-500/30 rounded-3xl shadow-2xl overflow-hidden flex flex-col w-[calc(100vw-24px)] sm:w-full sm:max-w-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: '92vh',
        }}
      >
        {/* Header */}
        <div className="relative px-5 py-4 border-b border-white/[0.06] flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            data-testid="image-cropper-close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <h2 className="text-base font-bold text-white pr-12">{title}</h2>
          <p className="text-[12px] text-zinc-400 mt-0.5">
            Drag to position · pinch / scroll to zoom · we&apos;ll auto-optimize for every device.
          </p>
        </div>

        {/* Body — flex column on mobile, side-by-side on sm+ */}
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {/* Cropper canvas */}
          <div
            className="relative w-full aspect-[4/3] bg-black rounded-2xl overflow-hidden border border-white/10"
            data-testid="image-cropper-canvas"
          >
            {src && (
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={true}
                restrictPosition={true}
                objectFit="contain"
              />
            )}
            {/* Gold rule-of-thirds "safe zone" outline — hint to centre
                the dish. Sits above the cropper but is non-interactive. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-[12%] border border-yellow-300/55 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.0)]"
            />
          </div>

          {/* Zoom + reset controls */}
          <div className="mt-4 flex items-center gap-3" data-testid="image-cropper-controls">
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => setZoom((z) => Math.max(1, +(z - 0.1).toFixed(2)))}
              className="w-9 h-9 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] text-white inline-flex items-center justify-center"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-yellow-400"
              aria-label="Zoom"
              data-testid="image-cropper-zoom"
            />
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => setZoom((z) => Math.min(4, +(z + 0.1).toFixed(2)))}
              className="w-9 h-9 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] text-white inline-flex items-center justify-center"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={reset}
              aria-label="Reset"
              data-testid="image-cropper-reset"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] text-white/85 text-[12px] font-semibold"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>

          {/* Live "Preview as customer" row */}
          <div className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">
              Preview as customer
            </p>
            <div className="grid grid-cols-3 gap-3" data-testid="image-cropper-previews">
              <PreviewTile label="Menu card" sub="thumbnail" src={previewUri} shape="square" />
              <PreviewTile label="Product detail" sub="full image" src={previewUri} shape="cover" />
              <PreviewTile label="Mobile" sub="phone view" src={previewUri} shape="mobile" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/[0.06] flex items-center justify-between gap-3 flex-shrink-0 bg-black/30">
          <button
            type="button"
            onClick={onCancel}
            data-testid="image-cropper-cancel"
            className="px-4 h-10 rounded-xl bg-white/[0.06] hover:bg-white/[0.10] text-white text-[13px] font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!area || saving}
            onClick={save}
            data-testid="image-cropper-save"
            className="inline-flex items-center gap-1.5 px-5 h-10 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed text-black text-[13px] font-extrabold"
          >
            <Check className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save Photo'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const PreviewTile = ({ label, sub, src, shape }) => {
  // Each preview tile mirrors the actual menu render shape so the
  // restaurant owner sees EXACTLY what their customers will see.
  let wrap;
  if (shape === 'square') {
    wrap = 'aspect-square rounded-xl';
  } else if (shape === 'mobile') {
    // Phone-like rounded chrome — visualises how it'd appear in the
    // PublicMenu card thumbnail (small square thumbnail style).
    wrap = 'aspect-[3/5] rounded-xl';
  } else {
    wrap = 'aspect-[4/3] rounded-xl';
  }
  return (
    <div className="flex flex-col items-stretch">
      <div className={`${wrap} bg-black/40 border border-white/10 overflow-hidden flex items-center justify-center relative`}>
        {src ? (
          shape === 'mobile' ? (
            <div className="w-full h-full p-2 bg-zinc-950 flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-md overflow-hidden ring-1 ring-yellow-500/30">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="h-1.5 w-3/4 bg-white/[0.18] rounded mb-1" />
                  <div className="h-1.5 w-1/3 bg-white/[0.10] rounded" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-md overflow-hidden ring-1 ring-yellow-500/30">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="h-1.5 w-2/3 bg-white/[0.18] rounded mb-1" />
                  <div className="h-1.5 w-1/4 bg-white/[0.10] rounded" />
                </div>
              </div>
            </div>
          ) : (
            <img src={src} alt={label} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="text-zinc-600 flex flex-col items-center gap-1">
            {shape === 'mobile' ? <Smartphone className="w-5 h-5" /> : <ImgIcon className="w-5 h-5" />}
          </div>
        )}
      </div>
      <p className="mt-1.5 text-[10.5px] text-white font-semibold text-center leading-none">{label}</p>
      <p className="text-[9.5px] text-zinc-500 text-center mt-0.5">{sub}</p>
    </div>
  );
};

export default ImageCropperModal;
